/**
 * ENG-6918: which keys of an integration's `config` blob are CONTROL-PLANE ONLY —
 * bookkeeping the platform keeps about a connection, never anything the agent's
 * runtime consumes.
 *
 * WHY THIS EXISTS, and why it is one module rather than two lists.
 *
 * Two places need the same answer and used to answer it independently:
 *
 *   1. `cleanManagedReconnectConfig` (packages/api) — strips these from the DB row
 *      at reconnect, because a stale `<provider>_server_id` re-binds the agent to
 *      an old auth_config (ENG-6156).
 *   2. `writeIntegrations` (the claude-code adapter) — decides what lands in the
 *      agent's `.env.integrations`.
 *
 * (2) had no such concept at all. It published EVERY string field of `config`
 * verbatim, so the control plane's own bookkeeping became agent environment:
 *
 *     managed_state_token   -> COMPOSIO_SALESFORCE_MANAGED_STATE_TOKEN
 *     managed_state_expires -> COMPOSIO_SALESFORCE_MANAGED_STATE_EXPIRES
 *     composio_server_id    -> COMPOSIO_SALESFORCE_SERVER_ID
 *
 * THE INCIDENT THAT MADE THIS LOAD-BEARING. `managed_state_token` is a one-shot
 * OAuth CSRF token: minted at initiate, matched at callback, then CONSUMED and
 * stripped from `config` (ENG-7272 made it a bounded list; ENG-8582 governs when
 * it may be cleared). So across provision polls the key is present, then absent,
 * then present again.
 *
 * The adapter's publish guard is `typeof value === 'string' && value`, so an
 * absent key does not become an empty env var — the var VANISHES from the file.
 * `classifyEnvIntegrationsDiff` (apps/cli) can only read that as `added` /
 * `removed`, which the restart breaker treats as integration-set MEMBERSHIP churn
 * and counts on the provisioning tally. Enough of them inside the window and the
 * agent is auto-paused.
 *
 * That is ENG-8679's Acquire Intelligence incident: `proposal-builder-comprehensive`
 * paused 44 minutes after creation, on 8 restarts whose "changed vars" were these
 * keys oscillating while its operator was connecting integrations in the console.
 *
 * NOTE THE MISDIAGNOSIS THIS FIXES, because it points at the wrong fix. ENG-8679
 * describes the cause as "a rotating credential is classified as a config change",
 * which predicts widening the ENG-7541 `rotated` exemption. That would change
 * nothing: `rotated` means same key, NEW VALUE, and these keys are never in that
 * bucket — they are add/remove, by construction. The classifier is not wrong; a
 * var with no consumer should never have been in the file to churn.
 *
 * WHY REMOVAL IS SAFE, verified rather than assumed (2026-08-11, at this commit):
 *   - no `${...SERVER_ID}` reference in packages/core, packages/api, apps/cli, or
 *     the seed catalog (packages/supabase/seeds/*.json)
 *   - no `MANAGED_STATE` reference anywhere under packages/, apps/ or templates/
 *     outside tests
 * Nothing reads them. A CSRF state token in an agent's environment was never a
 * feature; removing it is also a credential-hygiene improvement in its own right.
 *
 * If a future integration genuinely needs one of these values at runtime, do NOT
 * delete its entry here — publish that value under its own purpose-named config
 * key. These names mean "control-plane bookkeeping", and the whole point is that
 * the meaning is stable enough for two subsystems to share it.
 */

/**
 * Exact `config` keys that are control-plane only.
 *
 * The OAuth handshake scratch fields: the CSRF state token(s) minted at initiate
 * and matched at callback. `managed_state_tokens` is the bounded recent-token
 * list that superseded the single slot (ENG-7272); it is an array rather than a
 * string, so the adapter never published it — it is listed for completeness and
 * so the reconnect sanitiser and this set stay literally identical.
 *
 * ENG-9206 adds `pending_approval_at`, the instant a consent-gated connect was
 * initiated. Membership here buys both of the things it needs, from one edit:
 *
 *   - Every activation path (`/managed-callback`, `/managed-connect-api-key`,
 *     `/managed-poll`) already routes its config through
 *     `cleanManagedReconnectConfig`, so the marker is cleared the moment a
 *     connected account is recorded. A stale marker on a live row would make
 *     the phase derivation describe a wait that has already ended.
 *   - It never reaches the agent's `.env.integrations`. When the connect
 *     happened is control-plane bookkeeping; an agent has no use for it, and
 *     ENG-8679 is the standing reminder that a var with no consumer is churn
 *     that restarts sessions for nothing.
 */
export const CONTROL_PLANE_ONLY_CONFIG_KEYS: ReadonlySet<string> = new Set([
  'managed_state_token',
  'managed_state_expires',
  'managed_state_tokens',
  'pending_approval_at',
]);

/**
 * Suffix form: any `<provider>_server_id` (e.g. `composio_server_id`).
 *
 * A suffix rule rather than an enumeration because the provider prefix is
 * open-ended — composio, pipedream, and whatever is added next all mint one.
 */
export const CONTROL_PLANE_CONFIG_KEY_SUFFIXES: readonly string[] = ['_server_id'];

/**
 * True when `key` is control-plane bookkeeping that must not reach the agent.
 *
 * Case-insensitive on the key, because the two callers see it in different
 * cases: the API reads raw lower-case `config` keys, while the adapter has often
 * already upper-cased on its way to an env var name. A predicate that silently
 * missed one of those is precisely the drift this module exists to prevent.
 */
export function isControlPlaneOnlyConfigKey(key: string): boolean {
  const normalized = key.toLowerCase();
  if (CONTROL_PLANE_ONLY_CONFIG_KEYS.has(normalized)) return true;
  return CONTROL_PLANE_CONFIG_KEY_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
}

/**
 * True when an ENV VAR NAME is the published form of a control-plane-only config
 * key — `COMPOSIO_SALESFORCE_MANAGED_STATE_TOKEN`, `XERO__SECOND_SERVER_ID`, and
 * so on.
 *
 * SEPARATE FROM {@link isControlPlaneOnlyConfigKey}, and the difference is the
 * whole reason this function exists rather than reusing that one. The adapter
 * publishes under `${prefix}_${key}`, so the env name is PREFIXED — an exact-set
 * lookup for `managed_state_token` never matches
 * `composio_salesforce_managed_state_token`. Reusing the config-key predicate
 * here would silently match nothing, which looks exactly like "there was nothing
 * to filter".
 *
 * WHY THE CLI NEEDS THIS AT ALL — the rollout, which is the non-obvious half of
 * ENG-6918. Every agent provisioned before this change already has these vars in
 * its `.env.integrations`. The first poll after the fix REMOVES them, and a
 * removal is itself an add/remove diff — so the fix for a restart flap would,
 * without this, ship exactly one restart per agent across the whole fleet on
 * deploy. Dropping these names from the respawn set makes their disappearance a
 * no-op instead: nothing consumed them, so nothing needs to restart to notice
 * they are gone.
 *
 * Suffix-matched on purpose. The prefix is per-integration and per-connection
 * (ENG-8359 added a connection infix), so anchoring on the tail is the only form
 * that survives a named connection.
 */
export function isControlPlaneOnlyEnvVar(name: string): boolean {
  const normalized = name.toLowerCase();
  for (const key of CONTROL_PLANE_ONLY_CONFIG_KEYS) {
    if (normalized === key || normalized.endsWith(`_${key}`)) return true;
  }
  return CONTROL_PLANE_CONFIG_KEY_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
}
